/**
 * Octopus realUserData - Placeholder Implementation
 * 
 * This is a mock implementation that will be replaced with the real Octopus API.
 * All methods are placeholders that simulate the expected Octopus behavior.
 * 
 * HARDENED: Strict type-checking, explicit error throws, production-safe validation.
 */

/**
 * ServiceProvider interface structure
 * Represents a user profile with service provider information
 */
export interface ServiceProvider {
  userId: string;
  name?: string;
  email?: string;
  phone?: string;
  businessName?: string;
  services?: string[];
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

/**
 * Validates userId format: alphanumeric, 6-36 characters
 * Enforces: /^[A-Za-z0-9]{6,36}$/
 * @param userId - The user ID to validate
 * @returns true if valid, false otherwise
 */
function isValidUserIdFormat(userId: string): boolean {
  if (typeof userId !== 'string') {
    return false;
  }
  // Alphanumeric only, 6-36 characters - enforced regex
  const userIdRegex = /^[A-Za-z0-9]{6,36}$/;
  return userIdRegex.test(userId);
}

/**
 * Octopus realUserData class (placeholder)
 * This will be replaced with the actual Octopus implementation
 */
class Octopus {
  /**
   * Fetch user profile by userId
   * @param userId - The user ID to fetch
   * @returns Promise<ServiceProvider | null>
   * Returns null if userId is invalid (predictable response)
   */
  async getUserProfile(userId: string): Promise<ServiceProvider | null> {
    const timestamp = new Date().toISOString();
    
    // Runtime type guard: validate userId is string before any .trim() or use
    if (typeof userId !== 'string') {
      console.error(`[AUDIT] ${timestamp} realUserData.getUserProfile invalid userId: type ${typeof userId}`);
      return null; // Predictable: return null on invalid type
    }

    // Trim before validation
    const trimmedUserId = userId.trim();

    // Validate not empty after trimming
    if (!trimmedUserId || trimmedUserId === '') {
      console.error(`[AUDIT] ${timestamp} realUserData.getUserProfile invalid userId: empty`);
      return null; // Predictable: return null on empty
    }

    // Validate format using helper - enforced regex /^[A-Za-z0-9]{6,36}$/
    if (!isValidUserIdFormat(trimmedUserId)) {
      console.error(`[AUDIT] ${timestamp} realUserData.getUserProfile invalid userId: format invalid - ${trimmedUserId}`);
      return null; // Predictable: return null on invalid format
    }

    // Placeholder: Return mock profile
    // TODO: Replace with actual Octopus API call
    console.log(`[AUDIT] ${timestamp} realUserData: getUserProfile called (placeholder):`, { userId: trimmedUserId });

    // Mock profile - replace with real API call
    // Returns ONLY ServiceProvider | null as required - no side effects
    return {
      userId: trimmedUserId,
      name: `User ${trimmedUserId}`,
      email: `${trimmedUserId}@example.com`,
    };
  }

  /**
   * Connect two users (bidirectional connection)
   * @param scannerUserId - The user who scanned the QR code
   * @param scannedUserId - The user whose QR code was scanned
   * @returns Promise<void>
   * @throws Error if validation fails
   */
  async connectUsers(scannerUserId: string, scannedUserId: string): Promise<void> {
    const timestamp = new Date().toISOString();
    
    // Runtime type guard: validate scannerUserId is string
    if (typeof scannerUserId !== 'string') {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannerUserId must be a string, got ${typeof scannerUserId}`;
      console.error(error);
      throw new Error('Invalid scannerUserId type: must be a string');
    }

    // Runtime type guard: validate scannedUserId is string
    if (typeof scannedUserId !== 'string') {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannedUserId must be a string, got ${typeof scannedUserId}`;
      console.error(error);
      throw new Error('Invalid scannedUserId type: must be a string');
    }

    // Trim before validation
    const trimmedScannerUserId = scannerUserId.trim();
    const trimmedScannedUserId = scannedUserId.trim();

    // Validate not empty after trimming
    if (!trimmedScannerUserId || trimmedScannerUserId === '') {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannerUserId is empty`;
      console.error(error);
      throw new Error('scannerUserId cannot be empty');
    }

    if (!trimmedScannedUserId || trimmedScannedUserId === '') {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannedUserId is empty`;
      console.error(error);
      throw new Error('scannedUserId cannot be empty');
    }

    // Reject 'anonymous' - not a valid userId
    if (trimmedScannerUserId === 'anonymous' || trimmedScannedUserId === 'anonymous') {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers cannot use 'anonymous' as userId`;
      console.error(error);
      throw new Error('Cannot use anonymous as userId');
    }

    // Validate format using helper - enforced regex /^[A-Za-z0-9]{6,36}$/
    if (!isValidUserIdFormat(trimmedScannerUserId)) {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannerUserId format invalid: ${trimmedScannerUserId}`;
      console.error(error);
      throw new Error('scannerUserId must be alphanumeric, 6-36 characters');
    }

    if (!isValidUserIdFormat(trimmedScannedUserId)) {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers scannedUserId format invalid: ${trimmedScannedUserId}`;
      console.error(error);
      throw new Error('scannedUserId must be alphanumeric, 6-36 characters');
    }

    // Prevent self-connection (also checked here)
    if (trimmedScannerUserId === trimmedScannedUserId) {
      const error = `[AUDIT] ${timestamp} realUserData error: connectUsers cannot connect user to themselves: ${trimmedScannerUserId}`;
      console.error(error);
      throw new Error('Cannot connect user to themselves');
    }

    // Placeholder: Simulate connection creation
    // TODO: Replace with actual Octopus API call
    // Only perform contact/connection operations via Octopus-style methods
    console.log(`[AUDIT] ${timestamp} realUserData: connectUsers called (placeholder):`, {
      scannerUserId: trimmedScannerUserId,
      scannedUserId: trimmedScannedUserId
    });

    // Mock connection - replace with real API call
    // This would create a bidirectional connection in Octopus
    return Promise.resolve();
  }
}

/**
 * Export realUserData instance
 * This will be initialized with the real Octopus instance later
 */
export const realUserData = new Octopus();
